import { Location as AngularLocation, DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { Capacitor } from '@capacitor/core';
import { MATOMO_CONFIG, type MatomoConfig } from 'src/app/matomo/matomo.config';
import { MatomoConsentService } from 'src/app/matomo/matomo-consent.service';
import { isPublicWebAppBuild } from 'src/app/@shared/is-public-web-app';

type MatomoCommand = [string, ...unknown[]];
type MatomoWindow = Window & { _paq?: { push: (...commands: MatomoCommand[]) => unknown } };

@Injectable({
  providedIn: 'root',
})
export class MatomoService implements OnDestroy {
  private consented = false;
  private currentPageTitle: string = 'NAKOS Adresswelt';
  private currentPageUrl: string | null = null;
  private initialized = false;
  private lastTrackedUrl: string | null = null;
  private ready = false;
  private script: HTMLScriptElement | null = null;
  private subscriptions = new Subscription();
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private consent: MatomoConsentService,
    private location: AngularLocation,
    @Inject(MATOMO_CONFIG) private config: MatomoConfig | null,
  ) {}

  public init(): void {
    if (this.initialized || !this.config?.enabled || !isPublicWebAppBuild() || !this.consent.available
      || Capacitor.isNativePlatform() || !this.document.defaultView
      || !this.config.allowedHosts.includes(this.document.location.hostname)) {
      return;
    }

    this.initialized = true;
    this.subscriptions.add(this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.cancelScheduledPageView();
      }
    }));
    this.subscriptions.add(this.consent.getDecision().subscribe((decision) => {
      this.setConsent(decision === 'accepted');
    }));
  }

  public ngOnDestroy(): void {
    this.setConsent(false);
    this.subscriptions.unsubscribe();
    this.cancelScheduledPageView();
  }

  public trackPageView(pageTitle: string): void {
    this.currentPageTitle = pageTitle || 'NAKOS Adresswelt';
    this.currentPageUrl = this.getTrackingUrl();
    this.schedulePageView();
  }

  private setConsent(granted: boolean): void {
    if (this.consented === granted) {
      return;
    }

    this.consented = granted;
    this.cancelScheduledPageView();

    if (!granted) {
      this.lastTrackedUrl = null;

      if (this.ready) {
        // Revokes consent and deletes analytics cookies. Matomo may persist its opt-out cookie.
        this.push(['forgetConsentGiven']);
      }

      return;
    }

    if (!this.script) {
      this.loadTracker();

      return;
    }

    if (this.ready) {
      this.activateTracker();
    }
  }

  private loadTracker(): void {
    const view = this.document.defaultView as MatomoWindow;

    // Do not silently install a second tracker alongside an HTML/Tag Manager snippet.
    if (view._paq || this.document.querySelector('script[data-nakos-matomo]')) {
      console.error('Matomo already installed; use one tracking integration only.');

      return;
    }

    view._paq = [] as MatomoCommand[];
    const baseUrl = this.config!.trackerBaseUrl.replace(/\/$/u, '');

    this.push(['requireConsent']);
    this.push(['disableCookies']);
    this.push(['setTrackerUrl', `${baseUrl}/matomo.php`]);
    this.push(['setSiteId', this.config!.siteId]);
    this.push(['setReferrerUrl', '']);
    // Link listeners remain installed after revocation. Drop their requests before Matomo
    // can buffer them for a later consent grant; never replay activity from a denied period.
    this.push([
      'setCustomRequestProcessing',
      (request: string): string => {
        if (!this.consented) {
          return '';
        }

        return this.sanitizeRequest(request);
      },
    ]);

    this.script = this.document.createElement('script');
    this.script.async = true;
    this.script.src = `${baseUrl}/matomo.js`;
    this.script.dataset.nakosMatomo = 'true';
    this.script.referrerPolicy = 'no-referrer';
    this.script.onload = (): void => {
      this.ready = true;

      if (this.consented) {
        this.activateTracker();
      }
    };
    this.script.onerror = (): void => {
      // Fail closed; do not accumulate/retry a history of page views after a loading error.
      console.error('Matomo failed to load; no page views were sent.');
    };
    this.document.head.appendChild(this.script);
  }

  private activateTracker(): void {
    this.push(['setConsentGiven']);

    // setConsentGiven enables cookies internally, so reapply the selected cookie policy.
    if (this.config?.disableCookies) {
      this.push(['disableCookies']);
    }

    this.schedulePageView();
  }

  private schedulePageView(): void {
    this.cancelScheduledPageView();

    if (!this.consented || !this.ready || !this.router.navigated
      || !this.currentPageUrl || this.currentPageUrl !== this.getTrackingUrl()) {
      return;
    }

    // Ionic's view-enter event identifies the visible page after navigation.
    this.timer = setTimeout(() => {
      this.timer = null;
      this.trackCurrentPage();
    }, 0);
  }

  private trackCurrentPage(): void {
    if (!this.consented || !this.ready || this.router.getCurrentNavigation()) {
      return;
    }

    const url = this.getTrackingUrl();

    if (url !== this.currentPageUrl || url === this.lastTrackedUrl) {
      return;
    }

    this.push(['setReferrerUrl', this.lastTrackedUrl || '']);
    this.push(['setCustomUrl', url]);
    this.push(['setDocumentTitle', this.currentPageTitle]);
    // multiFieldSearch is registered on Matomo site 10: one page-view call also reports
    // approved searches. Do not additionally call trackSiteSearch for that same navigation.
    this.push(['trackPageView']);
    this.lastTrackedUrl = url;
  }

  private getTrackingUrl(): string {
    const view = this.document.defaultView!;
    const externalUrl = this.location.prepareExternalUrl(this.router.url);
    const url = new URL(externalUrl, view.location.origin);
    const keyword = url.searchParams.get('multiFieldSearch')?.trim();
    const [path] = this.router.url.split(/[?#]/u);
    const isSearchPage = (/^\/main\/(?:organizations\/selfHelpAddress\/[^/]+|topics)\/?$/u).test(path);

    // Free text, locations, filters, tokens and fragments are not implicitly approved analytics.
    url.search = '';
    url.hash = '';

    if (this.config?.trackSearch && isSearchPage && keyword) {
      url.searchParams.set('multiFieldSearch', keyword);
    }

    return url.href;
  }

  private sanitizeRequest(request: string): string {
    const params = new URLSearchParams(request);

    // Link tracking may run before the first page view sets Matomo's custom URL.
    // Normalize every request's page URL rather than sending the browser's raw query.
    if (params.has('url')) {
      params.set('url', this.getTrackingUrl());
    }

    // Outlinks/downloads can carry free text or tokens too. Keep their path, never their query.
    for (const key of ['link', 'download']) {
      const value = params.get(key);

      if (value) {
        const url = new URL(value, this.document.baseURI);

        url.search = '';
        url.hash = '';
        params.set(key, url.href);
      }
    }

    return params.toString();
  }

  private cancelScheduledPageView(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private push(command: MatomoCommand): void {
    (this.document.defaultView as MatomoWindow)._paq?.push(command);
  }
}
